<script>
import Layout from "../../layouts/main";
import PageHeader from "@/components/page-header";
import { ddlHelper, sessionHelper } from "@/utils";
import { SESSION } from "@/constants";
import { required } from "vuelidate/lib/validators";
import { mapState } from "vuex";
import i18n from '@/i18n'
import { LOOKUP_MODULE } from '@/state/modules/lookup'
import { PRODUCT_SOURCE_MODULE } from '@/state/modules/productSource'
import { lookupMethods, productSourceMethods } from "@/state/helpers";
import Multiselect from "vue-multiselect";

/**
 * Tabs & accordions component
 */
export default {
    page: {
        title: i18n.t("label.updateX", [i18n.t("module.productSource")]),
    },
    components: { Layout, PageHeader, Multiselect },
    beforeRouteLeave(to, from, next) {
        if (to.name != "Product-source-listing") {
            sessionHelper.clearSearch(SESSION.PRODUCT_SOURCE_SEARCH);
        }
        next();
    },
    computed: {
        ...mapState(PRODUCT_SOURCE_MODULE, ["status"]),
        ...mapState(LOOKUP_MODULE),
        data() {
            return this.$store.state.productSource.details;
        },
        productDdl() {
            return this.$store.state.lookup.productDdl;
        },
        sourceDdl() {
            return this.$store.state.lookup.sourceDdl;
        },
    },
    data() {
        return {
            title: i18n.t("label.updateX", [i18n.t("module.productSource")]),
            options: [
                { value: 0, text: 'Pending' },
                { value: 1, text: 'Approved' }
            ],
            items: [
                {
                    text: i18n.t('module.dashboard'),
                    href: "/",
                },
                {
                    text: i18n.t("module.productSource"),
                    href: "/product-source",
                },
                {
                    text: i18n.t("label.updateX", [i18n.t('module.productSource')]),
                    active: true,
                },
            ],
            submitted: false,
            permissions: sessionHelper.getPermissions(),
            updatePermission: null,
            readPermission: null,
            statusDdl: ddlHelper.getActivationStatus(true),
        };
    },
    validations: {
        data: {
            sourceID: { required },
            productID: { required },
        },
    },
    async created() {
        this.searchCriteria = sessionHelper.retrieveSearch(SESSION.PRODUCT_SOURCE_SEARCH);
        this.searchCriteriaProductSpecial = sessionHelper.retrieveSearch(SESSION.PRODUCT_SPECIAL_SEARCH);
        
        if (this.searchCriteriaProductSpecial != null && this.searchCriteriaProductSpecial != '') {
            this.isCloseWhenSuccess = this.searchCriteriaProductSpecial.isCloseWhenSuccess; 
        }
        else {
            this.isCloseWhenSuccess = false;
        }

        await this.validPermissions();
        this.lookupProduct({ hasOptionAll: false });
        this.lookupSource({ hasOptionAll: false });
        this.getProductSourceData();
    },
    methods: {
        ...productSourceMethods,
        ...lookupMethods,
        async validPermissions() {
            var jsonPermissions = JSON.parse(this.permissions)
            if (jsonPermissions.some(p => p.code === "RND_PS_PSU")) {
                this.updatePermission = true;
            }
            else {
                this.updatePermission = false;
            }

            if (jsonPermissions.some(p => p.code === "RND_PS_PSR")) {
                this.readPermission = true;
            }
            else {
                this.readPermission = false;
            }
        },
        async getProductSourceData() {
            this.isLoading = true;
            if (this.readPermission == true) {
                await this.getProductSource({ id: this.searchCriteria.selectedRecordId });
            }
            this.isLoading = false;
        },

        async requestUpdate() {
            this.submitted = true;
            // stop here if form is invalid
            this.$v.$touch();

            if (this.$v.data.$invalid) {
                console.log("Validation failed");

                for (const field in this.$v.data) {
                    if (this.$v.data[field].$invalid) {
                        console.log(`Validation failed for: ${field}`);
                    }
                }
                return;
            } else {
                let isCloseWhenSuccess = this.isCloseWhenSuccess; 

                let data = {
                    productID: this.data.productID.value,
                    sourceID: this.data.sourceID.value,
                    remark: this.data.remark,
                    id: this.data.uuid,
                };

                if (this.updatePermission == true) {
                    await this.updateProductSource({ data, isCloseWhenSuccess });
                }
            }
        },
    },
};
</script>

<template>
    <Layout>
        <PageHeader :title="title" :items="items" />
        <div class="row">
            <div class="col-lg-12">
                <div class="card">
                    <div class="card-body">
                        <form @submit.prevent="requestUpdate" v-if="readPermission == true">
                            <div class="row">
                                <div class="col-md-6">
                                    <b-form-group id="productID" label-cols-sm="4" label-cols-lg="4"
                                        :label="$t('module.product')" label-for="productID">
                                        <multiselect v-model="data.productID" deselect-label="Can't remove this value"
                                            track-by="value" label="text" placeholder="Select one" :options="productDdl"
                                            :searchable="true" :allow-empty="true">
                                        </multiselect>
                                        <div v-if="submitted && !$v.data.productID.required" class="invalid-feedback"
                                            style="display: block;">
                                            <span>{{ $t("message.requiredX", [$t("module.product")]) }}</span>
                                        </div>
                                    </b-form-group>
                                </div>
                                <div class="col-md-6">
                                    <b-form-group id="sourceID" label-cols-sm="4" label-cols-lg="4"
                                        :label="$t('module.source')" label-for="sourceID">
                                        <multiselect v-model="data.sourceID" deselect-label="Can't remove this value"
                                            track-by="value" label="text" placeholder="Select one" :options="sourceDdl"
                                            :searchable="true" :allow-empty="true">
                                        </multiselect>
                                        <div v-if="submitted && !$v.data.sourceID.required" class="invalid-feedback"
                                            style="display: block;">
                                            <span>{{ $t("message.requiredX", [$t("module.source")]) }}</span>
                                        </div>
                                    </b-form-group>
                                </div>

                                <div class="col-md-6">
                                    <b-form-group id="remark" label-cols-sm="4" label-cols-lg="4"
                                        :label="$t('label.remark')" label-for="remark">
                                        <textarea v-model="data.remark" class="form-control" name="textarea"
                                            rows="5"></textarea>
                                    </b-form-group>
                                </div>

                            </div>

                            <div class="mt-4 text-center" style="float: right">
                                <button class="btn btn-primary w-md waves-effect waves-light" style="margin-right: 10px"
                                    type="submit" :disabled="status.create || updatePermission != true">
                                    {{ $t("label.updateX", [$t("module.productSource")]) }}
                                </button>
                            </div>
                        </form>
                        <div style="text-align: center;" v-else>
                            {{ $t('message.noPermissionMessage') }}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </Layout>
</template>